import { useState, useEffect, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, Loader2, Search, X, Download } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, downloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DateRangePicker } from "@/components/views/DateRangePicker";
import { usePageView } from "@/hooks/usePageView";
import { usePermissions } from "@/hooks/usePermissions";
import { ViewToolbar } from "@/components/views/ViewToolbar";
import { DataView } from "@/components/views/DataView";

const ENTITY_LABEL = {
  role: "Role", member: "Member", report_recipient: "Report Recipient",
  org_settings: "Organisation", label_settings: "Labels", theme: "Theme",
  org_appearance: "Appearance", dashboard_template: "Dashboard Template", saved_view: "Saved View",
};

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}
function initialsOf(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

const ACTIVITY_PAGE = {
  key: "activity",
  defaultView: "compact",
  defaultSort: { key: "when", dir: "desc" },
  defaultGroupBy: "entity",
  dateField: "when",
  columns: [
    {
      key: "actor", label: "Actor", locked: true,
      accessor: (e) => e.actor_name || "System",
      cell: (e) => (
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-primary">{initialsOf(e.actor_name)}</span>
          <div className="min-w-0"><div className="truncate text-sm font-medium text-foreground">{e.actor_name || "System"}</div>
            {e.actor_role && <div className="text-xs text-muted-foreground">{e.actor_role}</div>}</div>
        </div>
      ),
    },
    { key: "action", label: "Action", accessor: (e) => e.action, cell: (e) => <span className="capitalize text-foreground">{(e.action || "").toLowerCase()}</span> },
    { key: "entity", label: "Section", groupable: true, accessor: (e) => ENTITY_LABEL[e.entity_type] || e.entity_type },
    { key: "reason", label: "Reason", defaultHidden: true, accessor: (e) => e.reason || "—" },
    {
      key: "when", label: "When", locked: true, type: "date",
      rawDate: (e) => e.created_at,
      accessor: (e) => relativeTime(e.created_at),
      cell: (e) => <span className="text-muted-foreground" title={e.created_at ? new Date(e.created_at).toLocaleString() : ""}>{relativeTime(e.created_at)}</span>,
    },
  ],
};

export default function Activity() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const pv = usePageView(ACTIVITY_PAGE);
  const perms = usePermissions();
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (start) sp.set("start", start);
    if (end) sp.set("end", end);
    try { const name = await downloadFile(`/export/activity.csv?${sp.toString()}`); toast.success("Activity exported", { description: name }); }
    catch (e) { toast.error(e.message); } finally { setExporting(false); }
  };

  const load = (params = {}) => {
    setLoading(true);
    const sp = new URLSearchParams({ limit: "200" });
    if (params.q ?? q) sp.set("q", params.q ?? q);
    if (params.start ?? start) sp.set("start", params.start ?? start);
    if (params.end ?? end) sp.set("end", params.end ?? end);
    apiFetch(`/audit?${sp.toString()}`).then(setEvents).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, start, end]);

  const hasFilters = q || start || end;
  const clearAll = () => { setQ(""); setStart(""); setEnd(""); };

  return (
    <div className="animate-fade-up">
      <PageHeader eyebrow="Team" title="Activity"
        subtitle="Search and date-filter the audit log to trace exactly who changed what and when. Audit history cannot be edited."
        actions={
          <div className="flex gap-2">
            {perms.has("Export Activity") && (
              <Button variant="outline" className="gap-2" onClick={exportCsv} disabled={exporting} data-testid="export-activity-csv">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
              </Button>
            )}
            <Button variant="outline" className="gap-2" onClick={() => load()} data-testid="refresh-activity">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        } />

      <Card className="mb-4 p-4 shadow-soft" data-testid="activity-controls">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Person, action, section…" className="pl-9" data-testid="activity-search" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Date range</Label>
            <DateRangePicker start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} testid="activity-range" />
          </div>
          {hasFilters && (
            <Button variant="ghost" className="gap-1.5 text-muted-foreground" onClick={clearAll} data-testid="activity-clear">
              <X className="h-4 w-4" /> Clear
            </Button>
          )}
        </div>
      </Card>

      <div className="mb-4 flex justify-end">
        <ViewToolbar pageDef={ACTIVITY_PAGE} pv={pv} />
      </div>

      {loading || !pv.loaded ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : (
        <div data-testid="activity-list">
          <DataView pageDef={ACTIVITY_PAGE} state={pv.state} update={pv.update} rows={events}
            emptyText={hasFilters ? "No activity matches your search or date range." : "No activity recorded yet. Changes will appear here as your team works."} />
        </div>
      )}
    </div>
  );
}
